/**
 * ComingSoonModal.tsx
 *
 * Purpose: Reusable "Coming Soon" modal for unimplemented features.
 * Created: 2026-01-22
 * Modified: 2026-01-22
 *
 * WHO: Users clicking on features that are not yet implemented.
 * WHAT: Displays a themed modal indicating the feature is in development.
 * WHERE: Mythbound.App/src/components/common/ComingSoonModal.tsx
 * WHY: Provides a consistent, on-brand experience for unavailable features.
 *
 * USAGE:
 * ```tsx
 * import { ComingSoonModal } from '../components/common/ComingSoonModal';
 *
 * const { isOpen, onOpen, onClose } = useDisclosure();
 *
 * <Button onClick={onOpen}>Map</Button>
 * <ComingSoonModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   featureName="Game Map"
 *   description="Interactive battle maps with fog of war"
 * />
 * ```
 *
 * INTEGRATION POINTS:
 * - SessionView.tsx: Used for Map button
 * - Dashboard.tsx: Used for any unimplemented Quick Actions
 * - Future components: Can be used anywhere features are planned
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  Icon,
} from '@chakra-ui/react';
import { TimeIcon } from '@chakra-ui/icons';
import { theme as mythboundTheme } from '../../config/theme';

// =============================================================================
// TYPES
// =============================================================================

export interface ComingSoonModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Name of the feature (e.g., "Game Map", "Spellbook") */
  featureName: string;
  /** Optional description of what the feature will do */
  description?: string;
  /** Optional custom icon (defaults to TimeIcon) */
  icon?: React.ReactElement;
}

// =============================================================================
// COMPONENT: ComingSoonModal
// =============================================================================

/**
 * ComingSoonModal Component
 *
 * WHO: Users attempting to access features still in development.
 * WHAT: Displays a themed "Coming Soon" message with feature details.
 * WHERE: Used throughout the app for unimplemented features.
 * WHY: Maintains immersion while communicating feature availability.
 *
 * ASYNC INTEGRATION:
 * - When a feature is implemented, remove the modal trigger
 * - Replace with actual feature functionality
 * - Keep this component for future planned features
 */
export const ComingSoonModal: React.FC<ComingSoonModalProps> = ({
  isOpen,
  onClose,
  featureName,
  description,
  icon,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
    >
      <ModalOverlay
        bg="rgba(0, 0, 0, 0.75)"
        backdropFilter="blur(4px)"
      />
      <ModalContent
        bg={mythboundTheme.colors.primary.dark}
        borderWidth={2}
        borderColor={mythboundTheme.colors.primary.gold}
        borderRadius="lg"
        boxShadow={mythboundTheme.shadows.goldGlow}
        mx={4}
      >
        {/* =================================================================
            Modal Header
        ================================================================= */}
        <ModalHeader
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.primary.gold}
          textAlign="center"
          pb={0}
        >
          {featureName}
        </ModalHeader>
        <ModalCloseButton color={mythboundTheme.colors.accent.silver} />

        {/* =================================================================
            Modal Body
        ================================================================= */}
        <ModalBody py={6}>
          <VStack spacing={4} align="center">
            {/* Icon */}
            <Box
              width="64px"
              height="64px"
              borderRadius="full"
              bg="rgba(217, 181, 85, 0.15)"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {icon || (
                <TimeIcon
                  boxSize={7}
                  color={mythboundTheme.colors.primary.gold}
                  aria-hidden="true"
                />
              )}
            </Box>

            {/* Coming Soon Text */}
            <Text
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              fontSize="lg"
              color={mythboundTheme.colors.secondary.white}
              textAlign="center"
            >
              Coming Soon
            </Text>

            {/* Description */}
            <Text
              fontSize="sm"
              color={mythboundTheme.colors.accent.silver}
              textAlign="center"
              maxW="280px"
            >
              {description || `The ${featureName} feature is being crafted by our artificers. Check back soon!`}
            </Text>

            {/* ===============================================================
                INTEGRATION POINT: Feature-specific preview
                When implementing a feature, you can add a preview image
                or additional information here before full implementation
            =============================================================== */}
          </VStack>
        </ModalBody>

        {/* =================================================================
            Modal Footer
        ================================================================= */}
        <ModalFooter justifyContent="center" pt={0}>
          <Button
            onClick={onClose}
            bg={mythboundTheme.colors.primary.gold}
            color={mythboundTheme.colors.primary.dark}
            fontWeight="bold"
            _hover={{
              bg: 'rgba(217, 181, 85, 0.85)',
              boxShadow: mythboundTheme.shadows.goldGlow,
            }}
          >
            Got It
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ComingSoonModal;
